import { useRef } from "react";

/**
 * Touch-only counterpart to the secret F2+O interest-reveal gesture.
 *
 * Desktop operators hold F2 and press O to flip an above-cap loan between the
 * displayed 1.16% cap and its true rate. Handhelds have no function keys, so on
 * coarse-pointer devices a DOUBLE TAP on the Interest header toggles the same
 * `revealInterest` flag. Deliberately touch-only: on desktop the keyboard combo
 * stays the sole way in, so a stray mouse double-click can never reveal a rate.
 *
 * Usage — spread the handlers on the header element and merge SECRET_TAP_SX into
 * its sx (the sx is what suppresses double-tap zoom and the selection callout):
 *
 *   const revealTap = useSecretRevealTap(onSecretReveal);
 *   <Box {...revealTap} sx={{ ...existing, ...SECRET_TAP_SX }} />
 */

// Max gap between the two taps to count as a double tap.
const DOUBLE_TAP_MS = 400;
// A tap that drags further than this was a scroll, not a tap.
const TAP_SLOP_PX = 12;

// Finger/stylus only — a mouse reports "fine".
const isCoarsePointer = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(pointer: coarse)").matches;

// Styles the target needs so the browser doesn't claim the gesture first:
// `manipulation` kills double-tap-to-zoom, the rest kill the long-press
// selection callout and the grey tap flash.
export const SECRET_TAP_SX = {
  touchAction: "manipulation",
  userSelect: "none",
  WebkitTapHighlightColor: "transparent",
  WebkitTouchCallout: "none",
};

export const useSecretRevealTap = (onReveal) => {
  const lastTapRef = useRef(0);
  const startRef = useRef(null);

  if (!onReveal) return {};

  const onTouchStart = (e) => {
    const t = e.touches && e.touches[0];
    startRef.current = t ? { x: t.clientX, y: t.clientY } : null;
  };

  const onTouchEnd = (e) => {
    if (!isCoarsePointer()) return;

    // Ignore taps that moved — those are scrolls passing through the header.
    const t = e.changedTouches && e.changedTouches[0];
    const start = startRef.current;
    startRef.current = null;
    if (t && start) {
      const moved =
        Math.abs(t.clientX - start.x) > TAP_SLOP_PX ||
        Math.abs(t.clientY - start.y) > TAP_SLOP_PX;
      if (moved) {
        lastTapRef.current = 0;
        return;
      }
    }

    const now = Date.now();
    if (now - lastTapRef.current <= DOUBLE_TAP_MS) {
      lastTapRef.current = 0;
      // Swallow the synthesized click and any residual zoom gesture.
      e.preventDefault();
      onReveal();
      // Silent confirmation that the gesture landed (no-op where unsupported).
      try {
        if (navigator.vibrate) navigator.vibrate(15);
      } catch {
        /* vibration is a nicety, never a requirement */
      }
    } else {
      lastTapRef.current = now;
    }
  };

  return { onTouchStart, onTouchEnd };
};
